'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Shield,
  FileText,
  AlertTriangle,
  Bot,
  Zap,
  Cookie,
  CreditCard,
  Ban,
  Database,
  Upload,
  ShieldCheck,
  Lightbulb,
  Mail,
  ChevronRight,
  ChevronDown,
  Sparkles,
  ArrowLeft,
} from 'lucide-react';

export interface TocSection {
  id: string;
  title: string;
}

interface PolicyLayoutProps {
  title: string;
  description: string;
  lastUpdated: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  sections: TocSection[];
  children: React.ReactNode;
  currentPath: string;
}

const policyLinks = [
  { href: '/privacy-policy', label: 'Privacy Policy', icon: Shield },
  { href: '/terms-and-conditions', label: 'Terms & Conditions', icon: FileText },
  { href: '/disclaimer', label: 'Disclaimer', icon: AlertTriangle },
  { href: '/ai-disclosure', label: 'AI Assistance Disclosure', icon: Bot },
  { href: '/auto-apply-safety', label: 'Auto-Apply Safety Policy', icon: Zap },
  { href: '/cookie-policy', label: 'Cookie Policy', icon: Cookie },
  { href: '/refund-policy', label: 'Refund & Billing Policy', icon: CreditCard },
  { href: '/acceptable-use', label: 'Acceptable Use Policy', icon: Ban },
  { href: '/data-retention', label: 'Data Retention Policy', icon: Database },
  { href: '/uploaded-content-policy', label: 'Uploaded Content Policy', icon: Upload },
  { href: '/trust-safety', label: 'Trust & Safety', icon: ShieldCheck },
  { href: '/intellectual-property', label: 'Intellectual Property', icon: Lightbulb },
  { href: '/contact-grievance', label: 'Contact & Grievance', icon: Mail },
];

function PublicHeader() {
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#6C63FF] to-[#8B5CF6] flex items-center justify-center">
            <Sparkles size={13} className="text-white" />
          </div>
          <span className="font-bold text-slate-900 text-sm">JobCompanion</span>
          <span className="px-2 py-0.5 rounded-full bg-[#6C63FF]/8 text-[#6C63FF] text-xs font-semibold border border-[#6C63FF]/15">Beta</span>
        </Link>
        <Link
          href="/legal"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#6C63FF] transition-colors"
        >
          <ArrowLeft size={14} />
          Legal Center
        </Link>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="bg-[#F8FAFC] border-t border-slate-100 py-10 mt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link href="/" className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#6C63FF] to-[#8B5CF6] flex items-center justify-center">
              <Sparkles size={11} className="text-white" />
            </div>
            <span className="font-semibold text-slate-700 text-sm">JobCompanion</span>
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5">
            {policyLinks.slice(0, 6).map((link) => (
              <Link key={link.href} href={link.href} className="text-xs text-slate-400 hover:text-slate-700 transition-colors">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
          <p>© 2026 JobCompanion. All rights reserved.</p>
          <p>AI-assisted job search. You always review before applying.</p>
        </div>
      </div>
    </footer>
  );
}

function PolicySidebar({ currentPath }: { currentPath: string }) {
  return (
    <nav className="space-y-0.5">
      <p className="px-3 mb-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
        Legal & Policies
      </p>
      {policyLinks.map((link) => {
        const NavIcon = link.icon;
        const isActive = currentPath === link.href;
        return (
          <Link
            key={link.href}
            href={link.href}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 ${
              isActive
                ? 'bg-[#6C63FF]/8 text-[#6C63FF] border-r-2 border-[#6C63FF]'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <NavIcon size={13} className="shrink-0" />
            <span className="truncate">{link.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function TableOfContents({ sections, activeId }: { sections: TocSection[]; activeId: string }) {
  return (
    <div className="space-y-0.5">
      <p className="px-3 mb-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
        On This Page
      </p>
      {sections.map((section) => (
        <a
          key={section.id}
          href={`#${section.id}`}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all duration-150 ${
            activeId === section.id
              ? 'text-[#6C63FF] font-medium bg-[#6C63FF]/5'
              : 'text-slate-400 hover:text-slate-700'
          }`}
        >
          <ChevronRight size={10} className="shrink-0" />
          <span className="truncate">{section.title}</span>
        </a>
      ))}
    </div>
  );
}

export default function PolicyLayout({
  title,
  description,
  lastUpdated,
  icon: PageIcon,
  iconBg,
  iconColor,
  sections,
  children,
  currentPath,
}: PolicyLayoutProps) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? '');
  const [mobilePolicyOpen, setMobilePolicyOpen] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Mobile accordion nav */}
        <div className="lg:hidden mb-6 space-y-2">
          <button
            onClick={() => setMobilePolicyOpen(!mobilePolicyOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-medium text-slate-700"
          >
            <span>All Legal Pages</span>
            <ChevronDown size={15} className={`transition-transform ${mobilePolicyOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobilePolicyOpen && (
            <div className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm">
              <PolicySidebar currentPath={currentPath} />
            </div>
          )}
          {sections.length > 0 && (
            <>
              <button
                onClick={() => setMobileTocOpen(!mobileTocOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-medium text-slate-700"
              >
                <span>On This Page</span>
                <ChevronDown size={15} className={`transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileTocOpen && (
                <div className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm">
                  <TableOfContents sections={sections} activeId={activeId} />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-8">
          {/* Left sidebar — policy links */}
          <aside className="hidden lg:block w-52 shrink-0">
            <div className="sticky top-20 bg-white border border-slate-100 rounded-2xl p-4 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-thin shadow-sm">
              <PolicySidebar currentPath={currentPath} />
            </div>
          </aside>

          {/* Main content — centered prose */}
          <main className="flex-1 min-w-0 max-w-2xl">
            {/* Page header */}
            <div className="mb-8">
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-11 h-11 rounded-2xl ${iconBg} flex items-center justify-center shrink-0`}>
                  <PageIcon size={20} className={iconColor} />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold text-slate-900 leading-tight">{title}</h1>
                  <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-400">Last updated: {lastUpdated}</span>
                <span className="w-1 h-1 rounded-full bg-slate-200" />
                <Link href="/legal" className="text-xs text-[#6C63FF] hover:underline">← Legal Center</Link>
              </div>
            </div>

            {/* Content */}
            <div className="prose-policy">
              {children}
            </div>
          </main>

          {/* Right sidebar — TOC */}
          {sections.length > 0 && (
            <aside className="hidden xl:block w-48 shrink-0">
              <div className="sticky top-20 bg-white border border-slate-100 rounded-2xl p-4 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-thin shadow-sm">
                <TableOfContents sections={sections} activeId={activeId} />
              </div>
            </aside>
          )}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}

/* ── Shared prose helpers ── */
export function PolicySection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-10 scroll-mt-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-3 pb-2.5 border-b border-slate-100">{title}</h2>
      <div className="space-y-3 text-sm text-slate-600 leading-relaxed">{children}</div>
    </section>
  );
}

export function PolicySubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-2">{title}</h3>
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

export function PolicyList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 ml-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#6C63FF]/40 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function PolicyCallout({ type, children }: { type: 'info' | 'warning' | 'success'; children: React.ReactNode }) {
  const styles = {
    info: 'bg-blue-50 border-blue-100 text-blue-800',
    warning: 'bg-amber-50 border-amber-100 text-amber-800',
    success: 'bg-emerald-50 border-emerald-100 text-emerald-800',
  };
  return (
    <div className={`rounded-xl border px-4 py-3.5 text-sm leading-relaxed ${styles[type]}`}>
      {children}
    </div>
  );
}

export function PolicyPlaceholder({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
      ⚠ {label}
    </span>
  );
}
